/**
 * Environment-specific URL utilities for the premium purchase feature
 */

/**
 * Path to a file under the `public/` folder (e.g. `/profiles/icon.svg`).
 * Uses Create React App's `process.env.PUBLIC_URL`: the **basename** the app is
 * served from (often `""`), not the API or marketing hostname. Same idea as
 * `%PUBLIC_URL%` in `public/index.html` at build time.
 */
export const getPublicAssetPath = (pathUnderPublic: string): string => {
    const prefix = process.env.PUBLIC_URL || '';
    const path = pathUnderPublic.startsWith('/') ? pathUnderPublic : `/${pathUnderPublic}`;
    return `${prefix}${path}`;
};

/** Hostnames that serve the public assessment SPA (legacy + short domain). */
export const ASSESSMENT_PUBLIC_HOSTNAMES = [
  'assessment.aptiveindex.com',
  'assessment2.aptiveindex.com',
  'aptive.in',
  'www.aptive.in',
] as const;

export const isAssessmentPublicHostname = (hostname: string): boolean =>
  ASSESSMENT_PUBLIC_HOSTNAMES.includes(hostname as (typeof ASSESSMENT_PUBLIC_HOSTNAMES)[number]);

export const getAssessmentBaseUrl = (): string => {
  const explicit = process.env.REACT_APP_ASSESSMENT_PUBLIC_BASE_URL?.trim();
  if (explicit) {
    return explicit.replace(/\/$/, '');
  }
  if (process.env.REACT_APP_ENV === 'staging') {
    return 'https://assessment2.aptiveindex.com';
  } else if (process.env.NODE_ENV === 'production') {
    return 'https://assessment.aptiveindex.com';
  } else {
    // Development - include /assessment for localhost
    return 'http://localhost:3000/assessment';
  }
};

/** Production short domain for copied assessment links when the org opts in. */
export const PRODUCTION_SHORT_ASSESSMENT_BASE_URL = 'https://aptive.in';

/** True when production should use aptive.in for org short links (not staging/dev). */
export const isProductionShortAssessmentEnvironment = (): boolean => {
  if (process.env.REACT_APP_ENV === 'staging') return false;
  if (process.env.NODE_ENV !== 'production') return false;
  return true;
};

/**
 * Base URL for copied/shared assessment links.
 * Org short-link setting uses aptive.in in production; other environments keep assessment hosts.
 */
export const getAssessmentLinkBaseUrl = (shortLinksEnabled = false): string => {
  if (shortLinksEnabled && isProductionShortAssessmentEnvironment()) {
    return PRODUCTION_SHORT_ASSESSMENT_BASE_URL;
  }
  return getAssessmentBaseUrl();
};

export const getAppBaseUrl = (): string => {
  if (process.env.REACT_APP_ENV === 'staging') {
    return 'https://app2.aptiveindex.com';
  } else if (process.env.NODE_ENV === 'production') {
    return 'https://app.aptiveindex.com';
  } else {
    // Development
    return 'http://localhost:3000';
  }
};

/**
 * Origin to keep the assessment flow on. Already on a public assessment host
 * (e.g. the short domain)? Stay there — jumping to the configured host would
 * drop per-origin localStorage mid-flow.
 */
const getCurrentAssessmentOrigin = (): string => {
  if (typeof window !== 'undefined' && isAssessmentPublicHostname(window.location.hostname)) {
    return window.location.origin;
  }
  return getAssessmentBaseUrl();
};

/**
 * Bookmarkable public results page, keyed by the per-result secret token from the submit
 * response. The server derives the link from the score, so no linkInfoUrl param is needed.
 */
export const constructResultsUrl = (resultsToken: string, purchased?: boolean): string => {
  const baseUrl = getCurrentAssessmentOrigin();
  return `${baseUrl}/results/${encodeURIComponent(resultsToken)}${purchased ? '?purchased=true' : ''}`;
};

export const constructAutoLoginUrl = (email: string, password: string): string => {
  const baseUrl = getAppBaseUrl();
  return `${baseUrl}/auto-login?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`;
}; 